import React, { useState, useRef } from "react";
import { Heart, Star, ChefHat, Edit, Users, Flame, Clock, Globe2, Share2, MoreHorizontal } from "lucide-react";
import { Button } from "../ui/button";
import { ButtonGroup } from "../../../components/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { InsetWindowMask } from "../media/InsetWindowMask";
import { CARD_RADIUS_PX, MEDIA_INSET_MOBILE_PX, MEDIA_INSET_DESKTOP_PX, CARD_BACKGROUND } from "../system/designTokens";
import { cn } from "../ui/utils";
import { MicroInteraction } from "../ui/micro-interactions";
import type { Recipe } from "../../../types/recipe";
import type { StandardRecipeV1 } from "../../../types/StandardRecipe";
import { toast } from "sonner";
import { MealCoursePicker } from "../discover/MealCoursePicker";

/**
 * Unified Recipe Card Component - Design System Compliant
 *
 * Consolidates all recipe card implementations with:
 * - Proper design system tokens (no hardcoded colors)
 * - Consistent animations (duration-fast, duration-normal)
 * - Standardized shadows (subtle, elevated, prominent)
 * - Multiple variants for different contexts
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

export type RecipeCardVariant = "compact" | "detailed" | "swipe";
export type RecipeCardSize = "sm" | "md" | "lg";

export interface RecipeCardFeatures {
  like?: boolean;
  addToPlan?: boolean;
  mealPicker?: boolean;
  swipe?: boolean;
  personalization?: boolean;
  rating?: boolean;
  difficulty?: boolean;
  tags?: boolean;
  share?: boolean;
}

export interface RecipeCardProps {
  recipe: Recipe | StandardRecipeV1;
  variant?: RecipeCardVariant;
  size?: RecipeCardSize;
  features?: RecipeCardFeatures;
  isLiked?: boolean;
  personalizationScore?: number;
  matchingFactors?: string[];
  onLike?: (recipeId: string) => void;
  onAddToPlan?: (recipeId: string, dayIndex: number, mealType: string) => void;
  onViewDetails?: (recipeId: string) => void;
  onEdit?: (recipeId: string) => void;
  onShare?: (recipeId: string) => void;
  className?: string;
  imageFetchPriority?: "high" | "low" | "auto";
  showDescription?: boolean;
  reserveDescription?: boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get difficulty badge styling using design system tokens
 */
const getDifficultyStyles = (difficulty?: string): string => {
  switch (difficulty?.toLowerCase()) {
    case "easy":
      return "bg-sage-100 text-sage-800 border-sage-200";
    case "medium":
      return "bg-coral-100 text-coral-800 border-coral-200";
    case "hard":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-lynx text-martinique border-stone/20";
  }
};

/**
 * Get recipe image with proper fallbacks
 */
const getRecipeImage = (recipe: Recipe | StandardRecipeV1): string => {
  if ("imageUrl" in recipe && recipe.imageUrl) return recipe.imageUrl;
  const src = (recipe as any).image || (recipe as any).heroImageUrl || "";
  if (src && typeof src === "string" && src.trim().length > 0) return src;
  // Fallback placeholder derived from title
  try {
    const { getRecipeImageUrl } = require("../../utils/recipeImage");
    const title = (recipe as any).title || (recipe as any).name || "recipe";
    return getRecipeImageUrl(String(title), { w: 800, h: 600 });
  } catch {
    return "https://picsum.photos/800/600";
  }
};

/**
 * Safely coerce a variety of time shapes (number or string like "45 min" or "30-45min") to minutes.
 */
const coerceMinutes = (v: unknown): number | undefined => {
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (!s) return undefined;
    // Grab the first integer in the string (e.g., "45 min", "30-45min" -> 30)
    const match = s.match(/\d+/);
    const n = match ? Number(match[0]) : Number(s);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  return undefined;
};

/**
 * Calculate total time from recipe, handling differing field names and formats.
 */
const getTotalTime = (recipe: Recipe | StandardRecipeV1): number => {
  // Direct total minutes
  const total = coerceMinutes((recipe as any).totalMinutes) ?? coerceMinutes((recipe as any).totalTime) ?? coerceMinutes((recipe as any).time) ?? coerceMinutes((recipe as any).readyInMinutes);

  // Separate prep/cook fields
  const prep = coerceMinutes((recipe as any).prepTime);
  const cook = coerceMinutes((recipe as any).cookTime);

  if (Number.isFinite(total as number)) return total as number;
  if (Number.isFinite(prep as number) && Number.isFinite(cook as number))
    return (prep as number) + (cook as number);
  if (Number.isFinite(prep as number)) return prep as number;
  if (Number.isFinite(cook as number)) return cook as number;
  return 0;
};

// ============================================================================
// Main Component
// ============================================================================

export function UnifiedRecipeCard({
  recipe,
  variant = "detailed",
  size = "md",
  features = {
    like: true,
    addToPlan: true,
    rating: true,
    difficulty: true,
    tags: true,
    share: true,
  },
  isLiked = false,
  personalizationScore,
  matchingFactors = [],
  onLike,
  onAddToPlan,
  onViewDetails,
  onEdit,
  onShare,
  className,
  imageFetchPriority = "auto",
  showDescription = true,
  reserveDescription = true,
}: RecipeCardProps) {
  // ============================================================================
  // State & Refs
  // ============================================================================

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number>(0);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLike?.(recipe.id);
  };

  const handleViewDetails = () => {
    onViewDetails?.(recipe.id);
  };

  const canShare = features?.share ?? true;

  // Build a canonical URL for non-custom recipes
  const buildCanonicalUrl = (id: string) =>
    (typeof window !== 'undefined')
      ? new URL(`/recipe/${id}`, window.location.origin).toString()
      : `/recipe/${id}`;

  // For custom recipes we prefer using the share dialog/action to produce a shareSlug
  const ensureSharedUrl = async (): Promise<string> => {
    // Prefer parent-provided share flow if any
    if ((recipe as any)?.isCustom) {
      // If parent wants to control sharing (e.g., open dialog), invoke it
      if (onShare) {
        onShare(recipe.id);
        // Parent will handle further UI; return canonical for safety
        return buildCanonicalUrl(recipe.id);
      }
      try {
        // Lazy import to avoid client bundle bloat
        const ops = await import('wasp/client/operations');
        // Generate an unlisted share link by default
        const res: any = await (ops as any).shareRecipe({ recipeId: recipe.id, visibility: 'unlisted' });
        if (res?.shareUrl) return res.shareUrl as string;
        if (res?.shareSlug) {
          return (typeof window !== 'undefined')
            ? new URL(`/recipes/shared/${res.shareSlug}`, window.location.origin).toString()
            : `/recipes/shared/${res.shareSlug}`;
        }
      } catch (e) {
        // Fall through to canonical if share action fails
      }
    }
    return buildCanonicalUrl((recipe as any).id);
  };

  const handleShare = async (e?: React.MouseEvent) => {
    try {
      e?.stopPropagation();
      // Parent override
      if (onShare) return onShare(recipe.id);
      const url = await ensureSharedUrl();
      if (typeof window !== 'undefined' && (navigator as any)?.share) {
        await (navigator as any).share({ title: (recipe as any)?.title || 'Recipe', url });
        toast.success('Link shared');
      } else if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not share');
    }
  };

  // Swipe gesture handlers (for swipe variant)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!features.swipe) return;
    startX.current = e.touches[0].clientX;
    setIsSwipeActive(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!features.swipe || !isSwipeActive) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - startX.current;

    // Only allow left swipe for like action
    if (diff < 0) {
      const offset = Math.min(Math.abs(diff), 80);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (!features.swipe || !isSwipeActive) return;

    setIsSwipeActive(false);

    // If swiped more than 50px, trigger like action
    if (Math.abs(swipeOffset) > 50 && !isLiked) {
      onLike?.(recipe.id);
    }

    setSwipeOffset(0);
  };

  // ============================================================================
  // Computed Values
  // ============================================================================

  const totalTime = getTotalTime(recipe);
  const imageUrl = getRecipeImage(recipe);
  const calories =
    ("nutrition" in recipe && recipe.nutrition?.calories) ?? (recipe as any).calories ?? null;

  // Size variants
  const sizeStyles = {
    sm: "min-h-[280px]",
    md: "min-h-[320px]",
    lg: "min-h-[360px]",
  };

  const paddingStyles = {
    sm: "p-3",
    md: "p-4",
    lg: "p-5",
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderLikeButton = () => {
    if (!features.like || !onLike) return null;

    return (
      <Button
        size="sm"
        variant="ghost"
        className={cn(
          "absolute right-[8px] top-[8px] md:right-[16px] md:top-[16px] h-10 w-10 rounded-full p-0 shadow-elevated transition-all duration-fast",
          "active:scale-95 desktop-hover:scale-105",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2",
          isLiked
            ? "bg-coral text-white desktop-hover:bg-coral-600 desktop-hover:shadow-prominent"
            : "border-2 border-white bg-white/95 backdrop-blur-sm desktop-hover:border-coral desktop-hover:bg-white desktop-hover:shadow-prominent",
        )}
        onClick={handleLike}
        aria-label={
          isLiked
            ? `Unlike recipe: ${recipe.title}`
            : `Like recipe: ${recipe.title}`
        }
        aria-pressed={isLiked}
      >
        <Heart
          className={cn(
            "h-5 w-5 transition-all duration-fast",
            isLiked
              ? "scale-110 fill-current text-white"
              : "stroke-[2.5px] text-coral desktop-hover:scale-110",
          )}
        />
      </Button>
    );
  };

  const renderPersonalizationBadge = () => {
    if (!features.personalization || !personalizationScore) return null;

    return (
      <div className="absolute left-2 top-2">
        <Badge className="bg-sage text-white shadow-subtle">
          <Star className="mr-1 h-3 w-3 fill-current" />
          {Math.round(personalizationScore)}% Match
        </Badge>
      </div>
    );
  };

  const renderSourceBadge = () => {
    // Intentionally suppressed: do not render source badges in user-facing UI
    return null;
  };

  const renderEditButton = () => {
    // Only show edit button for custom recipes
    if ("origin" in recipe && recipe.origin === "custom" && onEdit) {
      return (
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            "absolute right-12 top-2 h-10 w-10 rounded-full p-0 shadow-elevated transition-all duration-fast",
            "active:scale-95 desktop-hover:scale-105",
            "focus-visible:ring-sage focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "desktop-hover:border-sage border-2 border-white bg-white/95 backdrop-blur-sm desktop-hover:bg-white desktop-hover:shadow-prominent",
          )}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(recipe.id);
          }}
          aria-label={`Edit recipe: ${recipe.title}`}
        >
          <Edit className="text-sage h-4 w-4 stroke-[2.5px]" />
        </Button>
      );
    }
    return null;
  };

  // No overlay share button: design choice to reduce visual crowding next to Like

  const formatMinutes = (mins: number | null | undefined) => {
    if (!mins || mins <= 0) return '—'
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (h > 0) return m ? `${h}h ${m}m` : `${h}h`
    return `${m}m`
  }

  const renderMetaRow = () => {
    const servings = (recipe as any).servings
    const cal = calories ? Math.round(Number(calories)) : null
    return (
      <div className="grid grid-cols-3 gap-2 text-xs text-martinique/60">
        <span className="flex items-center gap-1.5 truncate"><Users className="h-3.5 w-3.5" /> {servings ?? '—'}</span>
        <span className="flex items-center gap-1.5 truncate"><Flame className="h-3.5 w-3.5" /> {typeof cal === 'number' ? cal : '—'}</span>
        <span className="flex items-center gap-1.5 truncate"><Clock className="h-3.5 w-3.5" /> {formatMinutes(totalTime)}</span>
      </div>
    )
  }

  const renderCuisine = () => {
    if (!recipe.cuisine) return null;

    return (
      <div className="rounded-lg bg-coral-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-base">🌍</span>
          <div>
            <p className="text-xs font-medium text-coral-800">Cuisine</p>
            <p className="text-sm font-semibold text-coral-900">
              {recipe.cuisine}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderKeyIngredients = () => {
    if (!recipe.ingredients || recipe.ingredients.length === 0) return null;

    return (
      <div className="border-stone/20 bg-cream rounded-lg border p-3">
        <h4 className="mb-2 flex items-center gap-2 font-semibold text-martinique">
          <ChefHat className="h-4 w-4 text-coral" />
          Key ingredients
        </h4>
        <ul className="space-y-1.5 text-sm">
          {recipe.ingredients.slice(0, 4).map((ingredient: any, index) => (
            <li key={index} className="flex items-start gap-2">
              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral"></div>
              <span className="text-martinique/80">
                {typeof ingredient === "string"
                  ? ingredient
                  : "name" in ingredient
                    ? [ingredient.amount, ingredient.unit, ingredient.name]
                        .filter(Boolean)
                        .join(" ")
                    : ingredient.displayUnit ||
                      ingredient.notes ||
                      "Ingredient"}
              </span>
            </li>
          ))}
          {recipe.ingredients.length > 4 && (
            <li className="pt-1 text-xs font-medium text-coral">
              +{recipe.ingredients.length - 4} more ingredients
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderTags = () => {
    if (!features.tags || !recipe.tags || recipe.tags.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1.5">
        {recipe.tags.slice(0, 3).map((tag, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="bg-cream h-5 rounded-sm px-2 py-0.5 text-xs font-medium text-martinique/70"
          >
            {tag}
          </Badge>
        ))}
        {recipe.tags.length > 3 && (
          <Badge
            variant="secondary"
            className="bg-cream h-5 rounded-sm px-2 py-0.5 text-xs text-martinique/50"
          >
            +{recipe.tags.length - 3}
          </Badge>
        )}
      </div>
    );
  };

  const renderDifficulty = () => {
    if (!features.difficulty || !recipe.difficulty) return null;

    return (
      <Badge
        variant="outline"
        className={cn(
          "h-5 rounded-sm px-2 py-0.5 text-xs font-medium",
          getDifficultyStyles(recipe.difficulty),
        )}
      >
        {recipe.difficulty}
      </Badge>
    );
  };

  const renderMatchingFactors = () => {
    if (!features.personalization || matchingFactors.length === 0) return null;

    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-sage-800">
          Why this matches you:
        </p>
        <div className="flex flex-wrap gap-1">
          {matchingFactors.slice(0, 2).map((factor, index) => (
            <Badge
              key={index}
              variant="outline"
              className="h-5 border-sage-200 bg-sage-50 px-2 py-0.5 text-xs text-sage-800"
            >
              {factor}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Variant Renderers
  // ============================================================================

  const renderCompactCard = () => (
    <Card
      ref={cardRef}
      className={cn(
        "border-stone/20 group flex h-full cursor-pointer overflow-hidden rounded-xl border bg-card",
        "transition-all duration-normal desktop-hover:border-coral/20 desktop-hover:shadow-elevated",
        "touch-manipulation select-none",
        sizeStyles[size],
        className,
      )}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDetails(); }}
      tabIndex={0}
      role="button"
      aria-label={`View recipe: ${recipe.title}`}
    >
      <CardContent className="flex h-full flex-col p-0">
        {/* Image (media block with inset matte window on all sides) */}
          <div className={cn("relative") }>
          <div className="group relative hidden overflow-hidden rounded-xl md:block" style={{ aspectRatio: '4 / 3', background: CARD_BACKGROUND }}>
            <ImageWithFallback src={imageUrl} alt={recipe.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.03]" />
            <InsetWindowMask className="absolute inset-0" insets={MEDIA_INSET_DESKTOP_PX} radius={CARD_RADIUS_PX} background={CARD_BACKGROUND} />
          </div>
          <div className="group relative block overflow-hidden rounded-xl md:hidden" style={{ aspectRatio: '1 / 1', background: CARD_BACKGROUND }}>
            <ImageWithFallback src={imageUrl} alt={recipe.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.03]" />
            <InsetWindowMask className="absolute inset-0" insets={MEDIA_INSET_MOBILE_PX} radius={CARD_RADIUS_PX} background={CARD_BACKGROUND} />
          </div>
          {renderLikeButton()}
          {renderEditButton()}
          {renderSourceBadge()}
          {renderPersonalizationBadge()}
        </div>

        {/* Content */}
          <div className={cn("flex flex-1 flex-col", paddingStyles[size], "pt-[8px] md:pt-[16px]") }>
          {/* Title (2 lines reserved) */}
          <div className="min-h-[3.5rem]">
            <h3 className="line-clamp-2 font-header text-base font-semibold leading-tight text-martinique">
              {recipe.title}
            </h3>
          </div>

          {/* Compact metrics */}
          <div className="mt-auto space-y-2">
            {renderMetaRow()}
            <div className="flex items-center justify-between">{renderDifficulty()}</div>
            {/* Cuisine badge for compact */}
            {recipe.cuisine && (
              <div className="flex items-center gap-1.5 text-xs">
                <Globe2 className="h-3.5 w-3.5" />
                <span className="font-medium text-coral-800">{recipe.cuisine}</span>
              </div>
            )}
          </div>
          {(onViewDetails || canShare) && (
            <div className="pt-2 flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="More options"
                    title="More"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View
                  </DropdownMenuItem>
                  {canShare && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleShare();
                      }}
                    >
                      Share
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderDetailedCard = () => (
    <Card
      ref={cardRef}
      className={cn(
        "border-stone/20 group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border bg-card",
        "transition-all duration-normal desktop-hover:border-coral/20 desktop-hover:shadow-elevated",
        "touch-manipulation select-none",
        sizeStyles[size],
        className,
      )}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewDetails(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleViewDetails();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: isSwipeActive
          ? `translateX(-${swipeOffset}px)`
          : "translateX(0)",
      }}
      tabIndex={0}
      role="button"
      aria-label={`View recipe: ${recipe.title}`}
    >
      <CardContent className="flex h-full flex-col p-0">
        {/* Image (media block with inset matte window on all sides) */}
          <div className={cn("relative") }>
          <div className="group relative hidden overflow-hidden rounded-xl md:block" style={{ aspectRatio: '4 / 3', background: CARD_BACKGROUND }}>
            <ImageWithFallback src={imageUrl} alt={recipe.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.03]" />
            <InsetWindowMask className="absolute inset-0" insets={MEDIA_INSET_DESKTOP_PX} radius={CARD_RADIUS_PX} background={CARD_BACKGROUND} />
          </div>
          <div className="group relative block overflow-hidden rounded-xl md:hidden" style={{ aspectRatio: '1 / 1', background: CARD_BACKGROUND }}>
            <ImageWithFallback src={imageUrl} alt={recipe.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.03]" />
            <InsetWindowMask className="absolute inset-0" insets={MEDIA_INSET_MOBILE_PX} radius={CARD_RADIUS_PX} background={CARD_BACKGROUND} />
          </div>
          {renderLikeButton()}
          {renderEditButton()}
          {renderSourceBadge()}
          {renderPersonalizationBadge()}
        </div>

        {/* Content */}
        <div className={cn("flex flex-1 flex-col", paddingStyles[size], "pt-[8px] md:pt-[16px]") }>
          {/* Title, Description (2 lines each), Meta pinned */}
          <div className="mb-3">
            <div className="min-h-[3.5rem]">
              <h3 className="line-clamp-2 font-header text-lg font-semibold leading-tight text-martinique">
                {recipe.title}
              </h3>
            </div>
            <div className={cn(reserveDescription ? 'min-h-[2.5rem]' : undefined)}>
              {showDescription && recipe.description && (
                <div className="relative">
                  <p className="line-clamp-2 text-sm text-martinique/60">
                    {recipe.description}
                  </p>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-white to-transparent" />
                </div>
              )}
            </div>
            <div className="mt-2">{renderMetaRow()}</div>
          </div>

          {/* Cuisine */}
          {recipe.cuisine && <div className="mt-3">{renderCuisine()}</div>}

          {/* Tags */}
          {features.tags && recipe.tags && recipe.tags.length > 0 && (
            <div className="mt-3">{renderTags()}</div>
          )}

          {/* Difficulty */}
          {features.difficulty && recipe.difficulty && (
            <div className="mt-2">{renderDifficulty()}</div>
          )}

          {/* Key Ingredients */}
          {recipe.ingredients && recipe.ingredients.length > 0 && (
            <div className="mt-3">{renderKeyIngredients()}</div>
          )}

          {/* Matching factors */}
          {features.personalization && matchingFactors.length > 0 && (
            <div className="mt-3">{renderMatchingFactors()}</div>
          )}

          {/* Actions */}
          {(features.addToPlan || features.mealPicker || onViewDetails || canShare) && (
            <div className="mt-auto pt-3 flex flex-col gap-2">
              {/* Meal Picker - if enabled, use full day/meal selection */}
              {features.mealPicker && onAddToPlan && (
                <MealCoursePicker
                  onAddToPlan={(dayIndex: number, mealType: string) =>
                    onAddToPlan(recipe.id, dayIndex, mealType)
                  }
                />
              )}
              {/* Simple Add to Plan button - if enabled and not using meal picker */}
              {features.addToPlan && !features.mealPicker && onAddToPlan && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToPlan(recipe.id, 0, "dinner");
                  }}
                  size="sm"
                  variant="outline"
                >
                  Add to Plan
                </Button>
              )}
              {/* View and Share buttons */}
              {(onViewDetails || canShare) && (
                <ButtonGroup size="sm" variant="outline">
                  {onViewDetails && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails();
                      }}
                    >
                      View
                    </Button>
                  )}
                  {canShare && (
                    <Button onClick={(e) => handleShare(e)}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                  )}
                </ButtonGroup>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <MicroInteraction
      animation="scale"
      interaction="tap"
      scale={0.98}
      duration={150}
    >
      {variant === "compact" ? renderCompactCard() : renderDetailedCard()}
    </MicroInteraction>
  );
}

// Export for convenience
export default UnifiedRecipeCard;
